import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { db, auth as firebaseAuth, functions } from '../config/firebase';
import { doc, setDoc, getDoc, deleteDoc, collection, addDoc } from 'firebase/firestore';
import { sendEmailVerification, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { FiMail, FiLock, FiEye, FiEyeOff, FiUser, FiBriefcase, FiCheck, FiRefreshCw, FiArrowRight, FiBookOpen, FiPackage, FiShoppingCart, FiFileText, FiUsers, FiDollarSign, FiTrendingUp, FiLayers } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { claimSignup, releaseSignupClaim, findPartialCompany, buildSignupCompany, SIGNUP_CLAIM_STALE_MS } from '../utils/signupSetup';
import { DEFAULT_SIGNUP_SOURCES, OTHER_SOURCE } from '../config/signupSources';
import { initMetaPixel, trackMetaEvent } from '../utils/metaPixel';

// Steps shown on the post-verification "creating workspace" screen. The index
// aligns with `setupStage` (0 = verified/done, advances as completion runs).
const SETUP_STEPS = ['Account verified', 'Creating your company', 'Setting up your accounts', 'Preparing your dashboard'];

const Signup = () => {
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false); // "✓ Sent!" confirmation after a resend
  const [startingOver, setStartingOver] = useState(false);   // "Wrong email? Start over" in progress
  // ── WhatsApp-first OTP verification (Track 2) ──────────────────────────────
  // channel 'whatsapp' (default) sends a 6-digit code to WhatsApp; 'email' falls
  // back to the original email-link verification (always available, never removed).
  const [channel, setChannel] = useState('whatsapp');
  const [accountCreated, setAccountCreated] = useState(false); // Auth user exists → editing must not recreate it
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpError, setOtpError] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpMaskedTo, setOtpMaskedTo] = useState(''); // "•••• 2460" returned by the server
  const otpRefs = useRef([]);
  // Post-verification workspace-setup progress (0..3) for the stepped loading UI.
  const [setupStage, setSetupStage] = useState(0);
  // "How did you hear about us?" options — SuperAdmin-managed (platformConfig/
  // signupOptions), with a safe fallback so a pre-auth read failure never blocks signup.
  const [sourceOptions, setSourceOptions] = useState(DEFAULT_SIGNUP_SOURCES);
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'platformConfig', 'signupOptions'));
        const list = snap.exists() ? snap.data()?.heardFromOptions : null;
        if (Array.isArray(list) && list.length > 0) {
          setSourceOptions(list.filter(o => o && o !== OTHER_SOURCE));
        }
      } catch (_) { /* keep defaults — signup must never break on this */ }
    })();
  }, []);

  // QR-campaign attribution: when arriving from a scanned banner, the qrScan
  // Cloud Function forwards here with ?qr=<campaignId>&src=<campaignName>.
  // We stamp the campaign id on the new company (for signup counting) and
  // pre-select its name as the source.
  const [qrCampaignId, setQrCampaignId] = useState('');
  const [qrSource, setQrSource] = useState('');
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qr = params.get('qr');
      const src = (params.get('src') || '').trim();
      if (qr) setQrCampaignId(qr);
      if (src) { setQrSource(src); setFormData(prev => ({ ...prev, heardFrom: src })); }
    } catch (_) { /* no-op */ }
  }, []);

  // Meta Pixel — signup flow only (utils/metaPixel.js). Only the trial name and
  // the ad source (?src= of the link, else the source picked on the form) are
  // sent — never email, phone, name or company details.
  useEffect(() => { initMetaPixel(); }, []);
  const metaParams = (fallbackSource) => {
    let src = '';
    try { src = (new URLSearchParams(window.location.search).get('src') || '').trim(); } catch (_) { /* no-op */ }
    const source = src || fallbackSource || '';
    return source ? { content_name: 'AmalERP Trial', source } : { content_name: 'AmalERP Trial' };
  };

  const COUNTRY_CODES = [
    { code: '+92', country: 'Pakistan', flag: '🇵🇰', digits: 10 },
    { code: '+971', country: 'UAE', flag: '🇦🇪', digits: 9 },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', digits: 9 },
    { code: '+974', country: 'Qatar', flag: '🇶🇦', digits: 8 },
    { code: '+973', country: 'Bahrain', flag: '🇧🇭', digits: 8 },
    { code: '+968', country: 'Oman', flag: '🇴🇲', digits: 8 },
    { code: '+965', country: 'Kuwait', flag: '🇰🇼', digits: 8 },
    { code: '+44', country: 'UK', flag: '🇬🇧', digits: 10 },
    { code: '+1', country: 'USA/Canada', flag: '🇺🇸', digits: 10 },
    { code: '+91', country: 'India', flag: '🇮🇳', digits: 10 },
    { code: '+86', country: 'China', flag: '🇨🇳', digits: 11 },
    { code: '+90', country: 'Turkey', flag: '🇹🇷', digits: 10 },
    { code: '+49', country: 'Germany', flag: '🇩🇪', digits: 11 },
    { code: '+33', country: 'France', flag: '🇫🇷', digits: 9 },
    { code: '+61', country: 'Australia', flag: '🇦🇺', digits: 9 },
    { code: '+27', country: 'South Africa', flag: '🇿🇦', digits: 9 },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', digits: 10 },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', digits: 9 },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾', digits: 10 },
    { code: '+63', country: 'Philippines', flag: '🇵🇭', digits: 10 },
  ];

  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    waCountryCode: '',
    waNumber: '',
    companyName: '',
    companyEmail: '',
    companyPhone: '',
    website: '',
    ntn: '',
    gstNo: '',
    stnNo: '',
    address: '',
    city: '',
    country: '',
    businessType: '',
    heardFrom: '',
    heardFromOther: '',
    fiscalYearStart: ''
  });

  const { signup, switchCompany, completeSignup } = useAuth();
  const navigate = useNavigate();
  // Guards against creating two companies when completion fires more than once
  // (e.g. the 3s verification poll + the email-link opening a second tab).
  const completingRef = useRef(false);

  const getFinancialYearDates = (startMonth) => {
    const monthMap = {
      'January': 0, 'February': 1, 'March': 2, 'April': 3,
      'May': 4, 'June': 5, 'July': 6, 'August': 7,
      'September': 8, 'October': 9, 'November': 10, 'December': 11
    };
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    const startMonthIndex = monthMap[startMonth] || 6;
    let startYear = currentYear;
    if (currentMonth < startMonthIndex) startYear = currentYear - 1;
    const startDate = new Date(startYear, startMonthIndex, 1);
    const endDate = new Date(startYear + 1, startMonthIndex, 0);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      yearCode: `${startYear}-${(startYear + 1).toString().slice(-2)}`,
      yearName: `Financial Year ${startYear}-${(startYear + 1).toString().slice(-2)}`
    };
  };

  const completeSignupProcess = async () => {
    // Same-tab re-entry guard: the verification poll + on-load check can both
    // flip emailVerified; without this they could run completion twice.
    if (completingRef.current) return;
    completingRef.current = true;
    setLoading(true);
    setSetupStage(1);
    setError('');
    let iAmCreator = false;
    let claimedCompanyId = null;
    try {
      const user = firebaseAuth.currentUser;
      if (!user) throw new Error('No authenticated user');
      const userId = user.uid;

      // Read the signup record for the pre-generated company id + any missing
      // form data.
      let data = formData;
      let pendingCompanyId = null;
      try {
        const pendingDoc = await getDoc(doc(db, 'pendingSignups', userId));
        if (pendingDoc.exists()) {
          const pd = pendingDoc.data();
          pendingCompanyId = pd.companyId || null;
          if (!data.companyName) data = { ...formData, ...(pd.formData || {}) };
        }
      } catch (e) { /* fall through — creation can still proceed */ }

      // ── Atomic claim (the duplicate-signup fix) ───────────────────────────
      // The verification-poll tab and the email-link tab can both reach here at
      // the same instant. A transaction makes claiming all-or-nothing: exactly
      // ONE tab becomes the creator; the other waits and opens the company the
      // winner makes. A claim left behind by a tab that was closed / refreshed /
      // went offline half-way is taken over after a short while (it used to
      // block the signup for good — utils/signupSetup.js).
      let outcome = await claimSignup(userId, pendingCompanyId);
      claimedCompanyId = outcome.companyId;

      // Another tab is building it right now → wait for it to FINISH (its last
      // step sets users.companies), or take over if it has stopped.
      const waitStart = Date.now();
      while (outcome.action === 'wait') {
        if (Date.now() - waitStart > SIGNUP_CLAIM_STALE_MS + 30000) {
          setError('Setting up your account is taking longer than expected. Please refresh this page, or log in again in a minute.');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 2000));
        outcome = await claimSignup(userId, pendingCompanyId, { force: Date.now() - waitStart > SIGNUP_CLAIM_STALE_MS + 5000 });
        claimedCompanyId = outcome.companyId;
      }

      // Already created → open it and stop.
      if (outcome.action === 'open') {
        await switchCompany(outcome.companyId);
        completeSignup();
        trackMetaEvent('CompleteRegistration', metaParams(data.heardFrom), userId);
        await new Promise(resolve => setTimeout(resolve, 100));
        navigate('/dashboard');
        return;
      }

      // outcome.action === 'create' → this tab owns creation.
      iAmCreator = true;
      const companyId = claimedCompanyId;
      // A take-over / retry finishes what an earlier try started: the year and
      // role it already made are reused, and the company details are not
      // written again.
      const partial = outcome.resume ? await findPartialCompany(companyId, userId) : { exists: false };
      const financialYearId = partial.fyId || outcome.fyId || `fy_${Date.now()}`;
      const adminRoleId = partial.roleId || outcome.roleId || `role_admin_${Date.now()}`;
      // Fiscal year defaults to July (Pakistan standard) — no longer asked at signup.
      const fiscalYearStart = data.fiscalYearStart || 'July';
      const fyDates = getFinancialYearDates(fiscalYearStart);

      // Seed module enablement from the Super-Admin-managed template at
      // platformConfig/newCompanyDefaults. Falls back to {} (→ code defaults via
      // isModuleEnabledForCompany) when the super admin hasn't configured it yet
      // or the read is unavailable.
      let seededModules = {};
      if (!partial.exists) {
        try {
          const defSnap = await getDoc(doc(db, 'platformConfig', 'newCompanyDefaults'));
          if (defSnap.exists() && defSnap.data().modules) seededModules = defSnap.data().modules;
        } catch (e) { /* fall back to code defaults */ }
      }

      const whatsappFull = await buildSignupCompany({
        userId, companyId, financialYearId, adminRoleId, data, fyDates,
        writeCompany: !partial.exists,
        companyDoc: {
          companyName: data.companyName,
          address: data.address || '', city: data.city, country: data.country,
          phone: data.companyPhone || '', email: data.companyEmail || '',
          website: data.website, ntn: data.ntn, gstNo: data.gstNo, stnNo: data.stnNo, logo: null,
          currency: { code: 'PKR', name: 'Pakistani Rupee', symbol: 'Rs.', decimalPlaces: 2, quantityDecimals: 2, thousandSeparator: ',', decimalSeparator: '.', symbolPosition: 'before' },
          settings: { negativeInventory: 'warn', approvalWorkflow: true, fiscalYearStart, taxRates: { gst: 17, whtSupplies: 4.5, whtServices: 8 } },
          businessType: data.businessType, activeFinancialYearId: financialYearId,
          heardFrom: data.heardFrom === 'Other' ? (data.heardFromOther || 'Other') : (data.heardFrom || ''),
          qrCampaignId: qrCampaignId || null,
          enabledModules: seededModules,
          onboarding: { completed: false, skipped: false, lastStep: 'profile', steps: {} },
          ownerId: userId, status: 'active', createdAt: new Date().toISOString(), createdBy: userId,
          subscription: {
            plan: 'trial',
            trialEndDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            lastPaymentDate: null,
            nextPaymentDate: null,
            amount: 0,
            notes: ''
          }
        },
        onStage: setSetupStage,
      });

      // ── Non-essential work — in the BACKGROUND (does not block the dashboard).
      // The super-admin signup notification, the analytics event, and the
      // pending-record cleanup don't need to finish before the user is in.
      addDoc(collection(db, 'signupNotifications'), {
        userId, displayName: data.displayName, email: data.email,
        whatsappNumber: whatsappFull,
        companyName: data.companyName,
        companyEmail: data.companyEmail || '', companyPhone: data.companyPhone || '',
        website: data.website || '',
        ntn: data.ntn || '', gstNo: data.gstNo || '', stnNo: data.stnNo || '',
        businessType: data.businessType || '', address: data.address || '', city: data.city, country: data.country,
        heardFrom: data.heardFrom === 'Other' ? (data.heardFromOther || 'Other') : (data.heardFrom || ''),
        fiscalYearStart: data.fiscalYearStart, companyId,
        signedUpAt: new Date().toISOString(), status: 'completed'
      }).catch((e) => console.warn('Signup notification failed (non-fatal):', e?.message));

      deleteDoc(doc(db, 'pendingSignups', userId)).catch((e) => console.warn('Could not delete pending signup:', e?.message));

      // Google Ads / GA4 signup conversion (fire-and-forget).
      try { window.gtag?.('event', 'sign_up', { method: 'email', company_id: companyId }); } catch (e) { /* analytics must never block signup */ }

      await switchCompany(companyId);
      completeSignup();
      trackMetaEvent('CompleteRegistration', metaParams(data.heardFrom), userId);
      await new Promise(resolve => setTimeout(resolve, 100));
      navigate('/dashboard');
    } catch (error) {
      setError(error.message || 'Failed to complete signup');
      console.error(error);
      completingRef.current = false;
      // If WE claimed but failed before finishing, release the claim so a retry
      // can re-create with the SAME company id. The chart-of-accounts init is
      // idempotent (it skips when accounts already exist), so a retry never
      // duplicates the chart, and reusing the id means no second company row.
      if (iAmCreator && claimedCompanyId) {
        try {
          await releaseSignupClaim(firebaseAuth.currentUser.uid);
        } catch (e) { /* best effort — ignore (the claim then expires by itself) */ }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendSuccess(false);
    try {
      const user = firebaseAuth.currentUser;
      if (!user) { setError('Session expired. Please refresh and try again.'); return; }
      try {
        const sendVerification = httpsCallable(functions, 'sendVerificationEmail');
        await sendVerification({ continueUrl: window.location.origin + '/login' });
      } catch (cfErr) {
        console.warn('Cloud Function email failed, falling back to Firebase:', cfErr);
        await sendEmailVerification(user, { url: window.location.origin + '/login', handleCodeInApp: false });
      }
      setResendCooldown(60);
      setError('');
      setResendSuccess(true); // positive feedback so users know it actually sent
    } catch (error) {
      const msg = error?.code || error?.message || '';
      if (msg.includes('too-many-requests')) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError('Failed to resend verification email. ' + (error.message || ''));
        console.error('Resend verification error:', error);
      }
    }
  };

  // "I'll verify later" — clean exit to the login page (they can log in once the
  // email is verified). Signs out so no half-finished session lingers.
  const handleVerifyLater = async () => {
    try { await signOut(firebaseAuth); } catch (e) { /* ignore */ }
    navigate('/login');
  };

  // "Wrong email? Start over" — the account already exists (created on submit),
  // so we delete the just-created UNVERIFIED account (freeing that email) + its
  // pending record, then return to Step 1 with the form still filled so they only
  // fix the address. No company exists yet (creation happens after verification),
  // so nothing real is lost.
  const handleStartOver = async () => {
    if (startingOver) return;
    setStartingOver(true);
    setError('');
    const user = firebaseAuth.currentUser;
    try {
      if (user) {
        const uid = user.uid;
        try { await deleteDoc(doc(db, 'pendingSignups', uid)); } catch (e) { /* ignore */ }
        try { await deleteDoc(doc(db, 'users', uid)); } catch (e) { /* ignore */ }
        // Deleting the auth user needs a recent login — true here (just signed
        // up). If it can't (rare), just sign out; the old email stays registered
        // and they'd use a different one.
        try { await user.delete(); }
        catch (e) { try { await signOut(firebaseAuth); } catch (e2) { /* ignore */ } }
      }
    } catch (e) { /* ignore */ }
    completingRef.current = false;
    setEmailVerified(false);
    setResendCooldown(0);
    setResendSuccess(false);
    // The account is gone — a fresh submit must recreate it, so clear the OTP/
    // account state and return to a clean WhatsApp-first form.
    setAccountCreated(false);
    setChannel('whatsapp');
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    setOtpMaskedTo('');
    setStep(1);
    setStartingOver(false);
  };

  // ── OTP / channel helpers (Track 2) ────────────────────────────────────────
  const otpValue = otpDigits.join('');

  // Send the email-verification link (Cloud Function, Firebase fallback). Used by
  // the email channel and by the auto-fallback when WhatsApp can't send.
  const sendEmailNow = async () => {
    const user = firebaseAuth.currentUser;
    if (!user) throw new Error('Session expired. Please refresh and try again.');
    try {
      const sendVerification = httpsCallable(functions, 'sendVerificationEmail');
      await sendVerification({ continueUrl: window.location.origin + '/login' });
    } catch (cfErr) {
      await sendEmailVerification(user, { url: window.location.origin + '/login', handleCodeInApp: false });
    }
  };

  // Send a 6-digit WhatsApp OTP (server reads the number from pendingSignups).
  // Throws if WhatsApp is unavailable so callers can fall back to email.
  const sendWaOtp = async () => {
    const fn = httpsCallable(functions, 'sendSignupOtp');
    const res = await fn({});
    setOtpMaskedTo((res && res.data && res.data.to) || '');
  };

  // WhatsApp-first send with automatic email fallback. Never leaves the user
  // stuck: if WhatsApp fails for any reason, we email them and switch the view.
  const startVerification = async () => {
    if (channel === 'whatsapp') {
      try {
        await sendWaOtp();
        setResendCooldown(60);
        return;
      } catch (e) {
        setChannel('email');
        try { await sendEmailNow(); setResendCooldown(60); } catch (_) { /* ignore */ }
        setError("WhatsApp is unavailable right now — we've emailed you a verification link instead.");
        return;
      }
    }
    try { await sendEmailNow(); setResendCooldown(60); }
    catch (_) { setError('Account created, but sending the verification failed. Use the Resend button.'); }
  };

  const stripCode = (m) => (m || '').replace(/^.*?:\s*/, '');

  // Verify the typed OTP. On success flip emailVerified → the existing
  // completeSignupProcess effect creates the company (same proven path as email).
  const handleVerifyOtp = async () => {
    if (otpValue.length !== 6) { setOtpError('Enter the 6-digit code.'); return; }
    setOtpVerifying(true); setOtpError('');
    try {
      const fn = httpsCallable(functions, 'verifySignupOtp');
      await fn({ code: otpValue });
      const user = firebaseAuth.currentUser;
      if (user) { try { await user.reload(); } catch (_) { /* ignore */ } }
      setEmailVerified(true); // triggers completeSignupProcess
    } catch (e) {
      setOtpError(stripCode(e && e.message) || 'Verification failed. Please try again.');
      setOtpDigits(['', '', '', '', '', '']);
      if (otpRefs.current[0]) otpRefs.current[0].focus();
    } finally {
      setOtpVerifying(false);
    }
  };

  // Resend the WhatsApp code (email resend uses handleResendVerification below).
  const handleResendWaOtp = async () => {
    if (resendCooldown > 0 || otpSending) return;
    setOtpError(''); setError(''); setResendSuccess(false); setOtpSending(true);
    try { await sendWaOtp(); setResendCooldown(60); setResendSuccess(true); }
    catch (e) { setOtpError(stripCode(e && e.message) || 'Could not send code. Try email instead.'); }
    finally { setOtpSending(false); }
  };

  // Switch verification channel from the verify screen (escape hatches).
  const switchToEmail = async () => {
    setChannel('email'); setError(''); setOtpError(''); setResendSuccess(false); setResendCooldown(0);
    try { await sendEmailNow(); setResendCooldown(60); setResendSuccess(true); }
    catch (_) { setError('Could not send the email. Please try again.'); }
  };
  const switchToWhatsapp = async () => {
    setError(''); setOtpError(''); setResendSuccess(false); setResendCooldown(0);
    try { await sendWaOtp(); setChannel('whatsapp'); setResendCooldown(60); }
    catch (e) { setError(stripCode(e && e.message) || 'WhatsApp is unavailable right now. Please continue with email.'); }
  };

  // "Wrong number? Edit details" — back to the form WITHOUT deleting the account.
  // Email stays fixed (changing it needs Start over); on re-submit we detect the
  // existing account and only resend, never recreate.
  const handleEditDetails = () => {
    setError(''); setOtpError(''); setResendSuccess(false);
    setOtpDigits(['', '', '', '', '', '']);
    setStep(1);
  };

  // OTP box input handlers (6 boxes: auto-advance, backspace-to-previous, paste).
  const handleOtpChange = (idx, val) => {
    const digit = val.replace(/[^\d]/g, '').slice(-1);
    setOtpDigits(prev => { const next = [...prev]; next[idx] = digit; return next; });
    if (digit && idx < 5 && otpRefs.current[idx + 1]) otpRefs.current[idx + 1].focus();
  };
  const handleOtpKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !otpDigits[idx] && idx > 0 && otpRefs.current[idx - 1]) {
      otpRefs.current[idx - 1].focus();
    }
  };
  const handleOtpPaste = (e) => {
    const text = ((e.clipboardData && e.clipboardData.getData('text')) || '').replace(/[^\d]/g, '').slice(0, 6);
    if (!text) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < text.length; i++) next[i] = text[i];
    setOtpDigits(next);
    const focusIdx = Math.min(text.length, 5);
    if (otpRefs.current[focusIdx]) otpRefs.current[focusIdx].focus();
  };

  // Email-verification poll — ONLY on the email channel (WhatsApp verifies via
  // the typed code, so it doesn't need the 3s reload poll).
  useEffect(() => {
    if (step !== 2 || emailVerified || channel !== 'email') return;
    const interval = setInterval(async () => {
      const user = firebaseAuth.currentUser;
      if (user) { await user.reload(); if (user.emailVerified) { setEmailVerified(true); clearInterval(interval); } }
    }, 3000);
    return () => clearInterval(interval);
  }, [step, emailVerified, channel]);

  // Auto-verify once all 6 digits are entered (WhatsApp channel).
  useEffect(() => {
    if (channel === 'whatsapp' && step === 2 && !emailVerified && !otpVerifying && otpValue.length === 6) {
      handleVerifyOtp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpValue]);

  useEffect(() => {
    if (emailVerified && step === 2) completeSignupProcess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailVerified]);

  useEffect(() => {
    const checkPendingSignup = async () => {
      const user = firebaseAuth.currentUser;
      if (!user) return;
      try {
        const pendingDoc = await getDoc(doc(db, 'pendingSignups', user.uid));
        if (!pendingDoc.exists()) return;
        const pendingData = pendingDoc.data();
        setFormData(prev => ({ ...prev, ...pendingData.formData }));
        // A pending record + a live session means this account already exists
        // (an in-progress signup, possibly reopened) — so "Edit details" must
        // resend, not recreate it.
        setAccountCreated(true);
        if (user.emailVerified) { setEmailVerified(true); setStep(2); } else { setStep(2); }
      } catch (err) { console.error('Error checking pending signup:', err); }
    };
    checkPendingSignup();
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    let { name, value } = e.target;
    if (name === 'waNumber') {
      value = value.replace(/[^\d]/g, '');
      if (value.startsWith('0')) value = value.slice(1);
    }
    setFormData({ ...formData, [name]: value });
  };

  // Single-screen signup: only the 4 mandatory fields + password.
  // Everything else (tax IDs, address, fiscal year, etc.) uses smart defaults
  // and is collected later in Settings / when first needed.
  const validateForm = () => {
    if (!formData.displayName || !formData.companyName || !formData.email || !formData.password) { setError('Please fill in all required fields'); return false; }
    if (!formData.waCountryCode) { setError('Please select your WhatsApp country code'); return false; }
    if (!formData.waNumber) { setError('WhatsApp number is required'); return false; }
    const waDigits = formData.waNumber.replace(/[\s\-()]/g, '');
    const selectedCountry = COUNTRY_CODES.find(c => c.code === formData.waCountryCode);
    if (!/^\d+$/.test(waDigits)) { setError('WhatsApp number should contain only digits (without country code)'); return false; }
    if (waDigits.startsWith('0')) { setError('Do not start with 0. Enter number without trunk prefix (e.g. 3353058349 not 03353058349)'); return false; }
    if (selectedCountry && waDigits.length !== selectedCountry.digits) { setError(`${selectedCountry.country} WhatsApp number must be ${selectedCountry.digits} digits (you entered ${waDigits.length})`); return false; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return false; }
    if (!formData.heardFrom) { setError('Please tell us how you heard about AmalERP'); return false; }
    if (formData.heardFrom === OTHER_SOURCE && !formData.heardFromOther.trim()) { setError('Please mention how you heard about us'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setError('');
    setLoading(true);
    try {
      const { password, confirmPassword, ...safeFormData } = formData;
      const existing = firebaseAuth.currentUser;
      if (existing && accountCreated) {
        // Editing details after the account was already created (e.g. fixing a
        // typo'd WhatsApp number) — DO NOT recreate; just refresh the pending
        // record so the code goes to the corrected number.
        await setDoc(doc(db, 'pendingSignups', existing.uid), { formData: safeFormData }, { merge: true });
      } else {
        const whatsappFull = `${formData.waCountryCode} ${formData.waNumber}`.trim();
        const userCredential = await signup(formData.email, formData.password, {
          displayName: formData.displayName, whatsappNumber: whatsappFull, companies: []
        });
        const user = userCredential.user;
        // Pre-generate the company id ONCE here (single tab, single click) so
        // completion converges on the SAME company. The atomic claim then
        // guarantees only one tab actually creates it.
        const preCompanyId = `company_${Date.now()}`;
        await setDoc(doc(db, 'pendingSignups', user.uid), { formData: safeFormData, companyId: preCompanyId, createdAt: new Date().toISOString(), status: 'pending_verification' });
        setAccountCreated(true);
        trackMetaEvent('Lead', metaParams(formData.heardFrom), `lead_${user.uid}`);
      }
      setStep(2);
      // WhatsApp-first, with automatic email fallback if WhatsApp can't send.
      await startVerification();
    } catch (error) {
      setError(error.message || 'Failed to create account');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    { icon: <FiBookOpen />, label: 'Accounting' },
    { icon: <FiPackage />, label: 'Inventory' },
    { icon: <FiShoppingCart />, label: 'Point of Sale' },
    { icon: <FiFileText />, label: 'Invoicing' },
    { icon: <FiUsers />, label: 'HR & Payroll' },
    { icon: <FiDollarSign />, label: 'Finance' },
    { icon: <FiTrendingUp />, label: 'Reports' },
    { icon: <FiLayers />, label: 'Multi-Branch' },
  ];

  const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } } };
  const itemVariants = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } } };

  const renderStep1 = () => (
    <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
      {accountCreated && (
        <EditBanner>✏️ Editing your details — fix your WhatsApp number below, then tap “Update &amp; Resend Code”.</EditBanner>
      )}
      <InputRow>
        <InputGroup>
          <Label>Full Name *</Label>
          <InputWrapper><IconWrap><FiUser /></IconWrap><Input type="text" name="displayName" placeholder="Enter your full name" value={formData.displayName} onChange={handleChange} /></InputWrapper>
        </InputGroup>
      </InputRow>
      <InputRow>
        <InputGroup>
          <Label>Company Name *</Label>
          <InputWrapper><IconWrap><FiBriefcase /></IconWrap><Input type="text" name="companyName" placeholder="Your business name" value={formData.companyName} onChange={handleChange} /></InputWrapper>
        </InputGroup>
      </InputRow>
      <InputRow>
        <InputGroup>
          <Label>Email Address *</Label>
          <InputWrapper><IconWrap><FiMail /></IconWrap><Input type="email" name="email" placeholder="you@company.com" value={formData.email} onChange={handleChange} disabled={accountCreated} /></InputWrapper>
          {accountCreated && <EditHint>To change your email, use “Start over” on the code screen.</EditHint>}
        </InputGroup>
      </InputRow>
      <InputRow>
        <InputGroup>
          <Label><FaWhatsapp style={{ color: '#25D366', marginRight: 4, verticalAlign: 'middle' }} /> WhatsApp Number *</Label>
          <WhatsAppRow>
            <CountryCodeSelect name="waCountryCode" value={formData.waCountryCode} onChange={handleChange}>
              <option value="">Select code</option>
              {COUNTRY_CODES.map(c => (
                <option key={c.code} value={c.code}>{c.flag} {c.code} {c.country}</option>
              ))}
            </CountryCodeSelect>
            <WhatsAppInput type="tel" name="waNumber" placeholder={(() => { const c = COUNTRY_CODES.find(x => x.code === formData.waCountryCode); return c ? '0'.repeat(c.digits) : '3001234567'; })()} value={formData.waNumber} onChange={handleChange} />
          </WhatsAppRow>
        </InputGroup>
      </InputRow>
      <InputRow>
        <InputGroup>
          <Label>How did you hear about AmalERP? *</Label>
          <Select name="heardFrom" value={formData.heardFrom} onChange={handleChange}>
            <option value="">-- Select --</option>
            {(qrSource && !sourceOptions.includes(qrSource) ? [qrSource, ...sourceOptions] : sourceOptions).map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
            <option value={OTHER_SOURCE}>Other (please mention)</option>
          </Select>
        </InputGroup>
      </InputRow>
      {formData.heardFrom === 'Other' && (
        <InputRow>
          <InputGroup>
            <Label>Please mention</Label>
            <Input type="text" name="heardFromOther" placeholder="How did you hear about us?" value={formData.heardFromOther} onChange={handleChange} />
          </InputGroup>
        </InputRow>
      )}
      <InputRow>
        <InputGroup>
          <Label>Password *</Label>
          <InputWrapper>
            <IconWrap><FiLock /></IconWrap>
            <Input type={showPassword ? 'text' : 'password'} name="password" placeholder="Create a password (min 6 characters)" value={formData.password} onChange={handleChange} disabled={accountCreated} />
            <PassToggle type="button" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <FiEyeOff /> : <FiEye />}</PassToggle>
          </InputWrapper>
        </InputGroup>
      </InputRow>
    </motion.div>
  );

  const renderStep3 = () => (
    <motion.div key="step3" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
      {emailVerified ? (
        /* Verified (WhatsApp OR email) → the existing completion flow runs */
        <VerificationBox>
          <SuccessIconWrap as={motion.div} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 300, damping: 15 }}>
            <FiCheck size={40} />
          </SuccessIconWrap>
          <VerifTitle>Verified! 🎉</VerifTitle>
          <VerifText>Setting up your workspace — just a moment…</VerifText>
          <SetupSteps>
            {SETUP_STEPS.map((label, i) => {
              const done = setupStage > i;
              const active = setupStage === i;
              return (
                <SetupStepRow key={i}>
                  <SetupStepIcon $done={done} $active={active}>
                    {done ? <FiCheck size={12} /> : active ? <SmallSpin /> : i + 1}
                  </SetupStepIcon>
                  <SetupStepLabel $done={done} $active={active}>{label}</SetupStepLabel>
                </SetupStepRow>
              );
            })}
          </SetupSteps>
        </VerificationBox>
      ) : channel === 'whatsapp' ? (
        /* ── WhatsApp OTP view (default) ── */
        <VerificationBox>
          <WaIconWrap as={motion.div} animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }}>
            <FaWhatsapp size={40} />
          </WaIconWrap>
          <VerifTitle>Enter WhatsApp Code</VerifTitle>
          <VerifText>We sent a 6-digit code on WhatsApp to</VerifText>
          <VerifEmail>{otpMaskedTo || `${formData.waCountryCode} ${formData.waNumber}`}</VerifEmail>

          <OtpRow onPaste={handleOtpPaste}>
            {otpDigits.map((d, i) => (
              <OtpBox
                key={i}
                ref={el => (otpRefs.current[i] = el)}
                type="tel"
                inputMode="numeric"
                maxLength={1}
                value={d}
                autoFocus={i === 0}
                $error={!!otpError}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
              />
            ))}
          </OtpRow>

          {otpError && <OtpErr>{otpError}</OtpErr>}

          <PrimaryBtn as={motion.button} type="button" onClick={handleVerifyOtp} disabled={otpVerifying || otpValue.length !== 6} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ width: '100%' }}>
            {otpVerifying ? <Spinner /> : <>Verify &amp; Continue <FiArrowRight /></>}
          </PrimaryBtn>

          {resendSuccess && <ResendOk><FiCheck size={13} /> New code sent.</ResendOk>}
          <ResendBtn type="button" onClick={handleResendWaOtp} disabled={resendCooldown > 0 || otpSending}>
            <FiRefreshCw size={14} />
            {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : (otpSending ? 'Sending…' : 'Resend code')}
          </ResendBtn>

          <SwitchRow>
            <SwitchLink type="button" onClick={switchToEmail}><FiMail size={13} /> Verify by email instead</SwitchLink>
          </SwitchRow>

          <ExitRow>
            <ExitLink type="button" onClick={handleEditDetails}>Wrong number? Edit details</ExitLink>
            <ExitDot>•</ExitDot>
            <ExitLink type="button" onClick={handleVerifyLater}>Finish later — Login</ExitLink>
            <ExitDot>•</ExitDot>
            <ExitLink type="button" onClick={handleStartOver} disabled={startingOver}>{startingOver ? 'Starting over…' : 'Start over'}</ExitLink>
          </ExitRow>
        </VerificationBox>
      ) : (
        /* ── Email view (original flow) + switch-to-WhatsApp ── */
        <VerificationBox>
          <VerifIconWrap as={motion.div} animate={{ scale: [1, 1.08, 1] }} transition={{ duration: 2, repeat: Infinity }}>
            <FiMail size={40} />
          </VerifIconWrap>
          <VerifTitle>Verify Your Email</VerifTitle>
          <VerifText>We've sent a verification link to</VerifText>
          <VerifEmail>{formData.email}</VerifEmail>

          <VerifSteps>
            <VerifStepRow><VerifStepNum>1</VerifStepNum><VerifStepText>Open the email we just sent you (from <b>AMAL ERP</b>).</VerifStepText></VerifStepRow>
            <VerifStepRow><VerifStepNum>2</VerifStepNum><VerifStepText>Tap the <b>“Verify Email”</b> button inside it.</VerifStepText></VerifStepRow>
            <VerifStepRow><VerifStepNum>3</VerifStepNum><VerifStepText>Come back to this page — it continues automatically.</VerifStepText></VerifStepRow>
          </VerifSteps>

          <SpamNote>📩 Can’t find it? Check your <b>Spam / Junk</b> folder and mark it “Not spam”.</SpamNote>

          <PollIndicator><SmallSpin /> Waiting for verification...</PollIndicator>

          {resendSuccess && <ResendOk><FiCheck size={13} /> Sent! Check your inbox and Spam folder.</ResendOk>}
          <ResendBtn type="button" onClick={handleResendVerification} disabled={resendCooldown > 0}>
            <FiRefreshCw size={14} />
            {resendCooldown > 0 ? `You can resend in ${resendCooldown}s` : 'Resend Verification Email'}
          </ResendBtn>

          <SwitchRow>
            <SwitchLink type="button" onClick={switchToWhatsapp}><FaWhatsapp size={13} /> Verify by WhatsApp instead</SwitchLink>
          </SwitchRow>

          <ExitRow>
            <ExitLink type="button" onClick={handleEditDetails}>Wrong details? Edit</ExitLink>
            <ExitDot>•</ExitDot>
            <ExitLink type="button" onClick={handleVerifyLater}>I’ll verify later — Login</ExitLink>
            <ExitDot>•</ExitDot>
            <ExitLink type="button" onClick={handleStartOver} disabled={startingOver}>{startingOver ? 'Starting over…' : 'Start over'}</ExitLink>
          </ExitRow>
        </VerificationBox>
      )}
    </motion.div>
  );

  const stepLabels = ['Account Details', 'Verification'];

  return (
    <Container>
      <BgOrb style={{ top: '10%', left: '5%' }} as={motion.div} animate={{ y: [0, -30, 0], x: [0, 15, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }} />
      <BgOrb style={{ bottom: '15%', right: '8%' }} as={motion.div} animate={{ y: [0, 20, 0], x: [0, -20, 0] }} transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }} $size="200px" $opacity="0.08" />
      <GridOverlay />

      <SignupCard as={motion.div} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}>
        {/* Left Panel */}
        <LeftPanel as={motion.div} variants={containerVariants} initial="hidden" animate="visible">
          <LeftContent>
            <motion.div variants={itemVariants}>
              <LogoWrapper>
                <LogoText>AMAL<LogoHighlight>ERP</LogoHighlight></LogoText>
                <LogoLine as={motion.div} initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 0.8, delay: 0.6 }} />
              </LogoWrapper>
            </motion.div>

            <motion.div variants={itemVariants}>
              <BrandTagline>Start Your Journey to</BrandTagline>
              <BrandTaglineBold>Better Business Management</BrandTaglineBold>
            </motion.div>

            {/* Step Indicator */}
            <motion.div variants={itemVariants}>
              <StepsBox>
                {stepLabels.map((label, i) => (
                  <StepRow key={i}>
                    <StepCircle $active={step >= i + 1} $done={step > i + 1} as={motion.div} animate={step === i + 1 ? { scale: [1, 1.1, 1] } : {}} transition={{ duration: 1.5, repeat: Infinity }}>
                      {step > i + 1 ? <FiCheck size={14} /> : i + 1}
                    </StepCircle>
                    <StepInfo>
                      <StepLabel $active={step >= i + 1}>{label}</StepLabel>
                    </StepInfo>
                    {i < 1 && <StepLine $active={step > i + 1} />}
                  </StepRow>
                ))}
              </StepsBox>
            </motion.div>

            {/* Module pills */}
            <motion.div variants={itemVariants}>
              <ModuleSectionLabel>Modules included</ModuleSectionLabel>
            </motion.div>
            <ModuleGrid variants={containerVariants}>
              {modules.map((mod, i) => (
                <ModulePill key={i} as={motion.div} variants={itemVariants}>
                  <ModulePillIcon>{mod.icon}</ModulePillIcon>
                  {mod.label}
                </ModulePill>
              ))}
            </ModuleGrid>
          </LeftContent>
        </LeftPanel>

        {/* Right Form Panel */}
        <RightPanel>
          <FormWrapper>
            <FormHeader as={motion.div} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
              <Title>{step === 2 ? 'Email Verification' : 'Create Your Account'}</Title>
              <Subtitle>Step {step} of 2 {step < 2 && `— ${stepLabels[step - 1]}`}</Subtitle>
            </FormHeader>

            <AnimatePresence>
              {error && (
                <motion.div initial={{ opacity: 0, y: -10, height: 0 }} animate={{ opacity: 1, y: 0, height: 'auto' }} exit={{ opacity: 0, y: -10, height: 0 }}>
                  <ErrorMsg>{error}</ErrorMsg>
                </motion.div>
              )}
            </AnimatePresence>

            <Form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {step === 1 && renderStep1()}
                {step === 2 && renderStep3()}
              </AnimatePresence>

              {step === 1 && (
                <BtnGroup>
                  <PrimaryBtn as={motion.button} type="submit" disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    {loading ? <Spinner /> : (accountCreated ? <>Update &amp; Resend Code <FiArrowRight /></> : <>Create Account &amp; Verify <FiArrowRight /></>)}
                  </PrimaryBtn>
                </BtnGroup>
              )}
            </Form>

            {step === 1 && (
              <BottomLink>Already have an account? <Link to="/login">Sign In</Link></BottomLink>
            )}
          </FormWrapper>
        </RightPanel>
      </SignupCard>

      <Footer as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
        AMALERP &mdash; Complete Accounting & ERP Software
      </Footer>
    </Container>
  );
};

// ─── Keyframes ────────────────────────────────────────────────
const gradientShift = keyframes`
  0%, 100% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
`;
const spin = keyframes`to { transform: rotate(360deg); }`;
const shimmer = keyframes`0% { background-position: -200% 0; } 100% { background-position: 200% 0; }`;

// ─── Styled Components ────────────────────────────────────────
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(-45deg, #1a0008, ${colors.primary.dark}, #2a0012, #0d0d0d);
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  padding: ${spacing.lg};
  position: relative;
  overflow: hidden;
`;

const GridOverlay = styled.div`
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(128, 0, 32, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(128, 0, 32, 0.03) 1px, transparent 1px);
  background-size: 60px 60px; pointer-events: none;
`;

const BgOrb = styled.div`
  position: absolute;
  width: ${p => p.$size || '150px'}; height: ${p => p.$size || '150px'};
  border-radius: 50%; background: radial-gradient(circle, ${colors.primary.main}, transparent);
  opacity: ${p => p.$opacity || '0.06'}; filter: blur(40px); pointer-events: none;
`;

const SignupCard = styled.div`
  display: flex; width: 100%; max-width: 1100px;
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05);
  overflow: hidden; min-height: 650px; position: relative; z-index: 1;
  @media (max-width: 768px) { flex-direction: column; min-height: auto; }
`;

const LeftPanel = styled.div`
  flex: 0 0 340px;
  background: linear-gradient(160deg, ${colors.primary.dark} 0%, ${colors.primary.main} 40%, ${colors.primary.light} 100%);
  padding: 40px 32px; color: ${colors.text.white};
  display: flex; flex-direction: column; justify-content: center;
  position: relative; overflow: hidden;
  &::before { content: ''; position: absolute; top: -50%; right: -30%; width: 80%; height: 80%; background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%); pointer-events: none; }
  @media (max-width: 768px) { flex: none; padding: 28px 24px; }
`;

const LeftContent = styled.div`
  position: relative; z-index: 1;
`;

const LogoWrapper = styled.div`
  display: inline-block; margin-bottom: 20px;
`;
const LogoText = styled.h1`
  font-size: 42px; font-weight: ${typography.fontWeight.bold};
  letter-spacing: 4px; line-height: 1; margin: 0;
  font-family: ${typography.fontFamily.heading}; color: #FFFFFF; text-transform: uppercase;
  @media (max-width: 768px) { font-size: 32px; }
`;
const LogoHighlight = styled.span`color: #FFD700;`;
const LogoLine = styled.div`
  height: 3px; background: linear-gradient(90deg, #FFD700, rgba(255, 215, 0, 0.2));
  border-radius: 2px; margin-top: 6px;
`;

const BrandTagline = styled.p`
  font-size: 14px; opacity: 0.7; margin-bottom: 2px; line-height: 1.3;
`;
const BrandTaglineBold = styled.p`
  font-size: 18px; font-weight: ${typography.fontWeight.bold};
  margin-bottom: 24px; line-height: 1.3; font-family: ${typography.fontFamily.heading};
`;

// Steps
const StepsBox = styled.div`
  display: flex; flex-direction: column; gap: 0; margin-bottom: 24px;
`;
const StepRow = styled.div`
  display: flex; align-items: center; gap: 10px; position: relative;
`;
const StepCircle = styled.div`
  width: 30px; height: 30px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: ${typography.fontWeight.bold};
  background: ${p => p.$done ? '#FFD700' : p.$active ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)'};
  color: ${p => p.$done ? '#5D0017' : '#fff'};
  border: 2px solid ${p => p.$done ? '#FFD700' : p.$active ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)'};
  transition: all 0.3s;
`;
const StepInfo = styled.div`flex: 1;`;
const StepLabel = styled.div`
  font-size: 13px; font-weight: ${p => p.$active ? typography.fontWeight.semibold : typography.fontWeight.regular};
  opacity: ${p => p.$active ? 1 : 0.45};
`;
const StepLine = styled.div`
  position: absolute; left: 14px; top: 32px; width: 2px; height: 16px;
  background: ${p => p.$active ? 'rgba(255,215,0,0.5)' : 'rgba(255,255,255,0.1)'};
`;

const ModuleSectionLabel = styled.div`
  font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px;
  opacity: 0.45; margin-bottom: 10px; font-weight: ${typography.fontWeight.medium};
`;
const ModuleGrid = styled(motion.div)`
  display: flex; flex-wrap: wrap; gap: 6px;
`;
const ModulePill = styled.div`
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px; padding: 5px 12px; font-size: 11px;
  font-weight: ${typography.fontWeight.medium}; white-space: nowrap;
`;
const ModulePillIcon = styled.span`
  display: flex; align-items: center; font-size: 11px; opacity: 0.8;
`;

// ─── Right Panel ──────────────────────────────────────────────
const RightPanel = styled.div`
  flex: 1; padding: 40px 44px;
  display: flex; align-items: flex-start; justify-content: center;
  background: #fff; overflow-y: auto; max-height: 100vh;
  @media (max-width: 768px) { padding: 28px 20px; }
`;

const FormWrapper = styled.div`
  width: 100%; max-width: 500px; padding: 8px 0;
`;

const FormHeader = styled.div`margin-bottom: 24px;`;
const Title = styled.h2`
  font-size: 24px; font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary}; margin-bottom: 4px;
  font-family: ${typography.fontFamily.heading};
`;
const Subtitle = styled.p`
  color: ${colors.text.secondary}; font-size: 13px;
`;

const ErrorMsg = styled.div`
  background: ${colors.status.error}10; color: ${colors.status.error};
  padding: 12px 14px; border-radius: 10px; margin-bottom: 16px;
  font-size: 13px; border: 1px solid ${colors.status.error}25;
  display: flex; align-items: center; gap: 8px;
  &::before { content: '!'; width: 20px; height: 20px; border-radius: 50%;
    background: ${colors.status.error}; color: white; display: flex; align-items: center;
    justify-content: center; font-size: 11px; font-weight: bold; flex-shrink: 0; }
`;

const Form = styled.form`
  display: flex; flex-direction: column; gap: 14px;
`;
const InputRow = styled.div`
  display: flex; gap: 12px; width: 100%;
`;
const InputGroup = styled.div`
  display: flex; flex-direction: column; gap: 5px;
  flex: ${p => p.$half ? '1' : 'auto'}; width: ${p => p.$half ? 'auto' : '100%'};
`;
const Label = styled.label`
  font-size: 12px; font-weight: ${typography.fontWeight.medium}; color: ${colors.text.primary};
`;
const InputWrapper = styled.div`
  position: relative; display: flex; align-items: center;
  border: 1.5px solid ${colors.border.light}; border-radius: 10px;
  background: #FAFAFA; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  &:focus-within { border-color: ${colors.primary.main}; background: ${colors.primary.main}04; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const IconWrap = styled.div`
  position: absolute; left: 12px; color: ${colors.text.tertiary};
  display: flex; align-items: center; font-size: 15px; transition: color 0.3s;
  ${InputWrapper}:focus-within & { color: ${colors.primary.main}; }
`;
const Input = styled.input`
  width: 100%; padding: 11px 12px 11px 38px;
  border: none; border-radius: 10px; font-size: 13px;
  background: transparent; color: ${colors.text.primary}; outline: none;
  &::placeholder { color: ${colors.text.disabled}; }
  /* For inputs without icons */
  ${InputGroup} > &:not(${InputWrapper} &) {
    border: 1.5px solid ${colors.border.light}; border-radius: 10px;
    background: #FAFAFA; padding: 11px 12px; transition: all 0.3s;
    &:focus { border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; background: ${colors.primary.main}04; }
  }
`;
const Select = styled.select`
  width: 100%; padding: 11px 12px;
  border: 1.5px solid ${colors.border.light}; border-radius: 10px;
  font-size: 13px; background: #FAFAFA; color: ${colors.text.primary};
  outline: none; transition: all 0.3s;
  &:focus { border-color: ${colors.primary.main}; box-shadow: 0 0 0 3px ${colors.primary.main}15; }
`;
const WhatsAppRow = styled.div`
  display: flex; gap: 8px; width: 100%;
`;
const CountryCodeSelect = styled.select`
  flex: 0 0 155px; padding: 11px 8px;
  border: 1.5px solid ${colors.border.light}; border-radius: 10px;
  font-size: 13px; background: #FAFAFA; color: ${colors.text.primary};
  outline: none; transition: all 0.3s; cursor: pointer;
  &:focus { border-color: #25D366; box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.15); }
`;
const WhatsAppInput = styled.input`
  flex: 1; padding: 11px 12px;
  border: 1.5px solid ${colors.border.light}; border-radius: 10px;
  font-size: 13px; background: #FAFAFA; color: ${colors.text.primary};
  outline: none; transition: all 0.3s; letter-spacing: 0.5px;
  &:focus { border-color: #25D366; box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.15); }
  &::placeholder { color: ${colors.text.disabled}; }
`;
const PassToggle = styled.button`
  position: absolute; right: 12px; background: none; border: none;
  color: ${colors.text.tertiary}; cursor: pointer; display: flex; align-items: center;
  font-size: 15px; padding: 0; transition: color 0.2s;
  &:hover { color: ${colors.primary.main}; }
`;

const BtnGroup = styled.div`
  display: flex; gap: 12px; margin-top: 8px;
`;
const BackBtn = styled.button`
  flex: 1; padding: 13px; background: #F5F5F5; color: ${colors.text.primary};
  font-size: 14px; font-weight: ${typography.fontWeight.medium};
  border: 1px solid ${colors.border.light}; border-radius: 12px;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;
  transition: all 0.2s;
  &:hover { background: ${colors.border.light}; }
`;
const PrimaryBtn = styled.button`
  flex: 2; padding: 13px;
  background: linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.light} 100%);
  color: ${colors.text.white}; font-size: 14px; font-weight: ${typography.fontWeight.semibold};
  border: none; border-radius: 12px; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  position: relative; overflow: hidden; transition: all 0.3s;
  &::before { content: ''; position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    background-size: 200% 100%; animation: ${shimmer} 3s infinite; }
  &:hover:not(:disabled) { box-shadow: 0 6px 20px ${colors.primary.main}40; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;
const Spinner = styled.div`
  width: 18px; height: 18px; border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white; border-radius: 50%; animation: ${spin} 0.6s linear infinite;
`;

const BottomLink = styled.p`
  text-align: center; margin-top: 20px; font-size: 13px; color: ${colors.text.secondary};
  a { color: ${colors.primary.main}; font-weight: ${typography.fontWeight.semibold}; text-decoration: none; &:hover { text-decoration: underline; } }
`;

// Verification
const VerificationBox = styled.div`
  display: flex; flex-direction: column; align-items: center;
  text-align: center; padding: 24px 0;
`;
const VerifIconWrap = styled.div`
  width: 72px; height: 72px; border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary.main}15, ${colors.primary.light}20);
  color: ${colors.primary.main}; display: flex; align-items: center;
  justify-content: center; margin-bottom: 20px;
`;
const SuccessIconWrap = styled.div`
  width: 72px; height: 72px; border-radius: 50%;
  background: ${colors.status.success}15; color: ${colors.status.success};
  display: flex; align-items: center; justify-content: center; margin-bottom: 20px;
`;
const VerifTitle = styled.h3`
  font-size: 22px; font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary}; margin-bottom: 8px; font-family: ${typography.fontFamily.heading};
`;
const VerifText = styled.p`
  font-size: 14px; color: ${colors.text.secondary}; margin-bottom: 4px;
`;
const VerifEmail = styled.p`
  font-size: 14px; font-weight: ${typography.fontWeight.semibold};
  color: ${colors.primary.main}; margin-bottom: 12px;
`;
const VerifHint = styled.p`
  font-size: 12px; color: ${colors.text.disabled}; margin-bottom: 20px;
  line-height: 1.6; max-width: 320px;
`;
const PollIndicator = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: ${colors.text.secondary}; margin-bottom: 16px;
`;
const SmallSpin = styled.div`
  width: 16px; height: 16px; border: 2px solid ${colors.border.light};
  border-top-color: ${colors.primary.main}; border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

// Stepped "creating workspace" progress list.
const SetupSteps = styled.div`
  width: 100%; max-width: 300px; margin: 20px auto 6px;
  display: flex; flex-direction: column; gap: 12px; text-align: left;
`;
const SetupStepRow = styled.div`
  display: flex; align-items: center; gap: 12px;
`;
const SetupStepIcon = styled.div`
  width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: ${typography.fontWeight.bold};
  background: ${p => (p.$done ? colors.success.main : p.$active ? `${colors.primary.main}15` : colors.border.light)};
  color: ${p => (p.$done ? '#fff' : p.$active ? colors.primary.main : colors.text.disabled)};
  transition: all 0.3s;
`;
const SetupStepLabel = styled.div`
  font-size: 13.5px;
  font-weight: ${p => (p.$active ? typography.fontWeight.semibold : typography.fontWeight.regular)};
  color: ${p => (p.$done || p.$active ? colors.text.primary : colors.text.disabled)};
  transition: all 0.3s;
`;
const ResendBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 8px 20px; background: transparent; color: ${colors.primary.main};
  font-size: 13px; font-weight: ${typography.fontWeight.medium};
  border: 1.5px solid ${colors.primary.main}; border-radius: 10px;
  cursor: pointer; transition: all 0.2s;
  &:hover:not(:disabled) { background: ${colors.primary.main}08; }
  &:disabled { opacity: 0.5; cursor: not-allowed; color: ${colors.text.disabled}; border-color: ${colors.border.light}; }
`;

const VerifSteps = styled.div`
  width: 100%; text-align: left; margin: 18px 0 12px;
  display: flex; flex-direction: column; gap: 10px;
`;
const VerifStepRow = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
`;
const VerifStepNum = styled.span`
  flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%;
  background: ${colors.primary.main}; color: #fff;
  font-size: 12px; font-weight: ${typography.fontWeight.bold};
  display: flex; align-items: center; justify-content: center; margin-top: 1px;
`;
const VerifStepText = styled.span`
  font-size: 13px; line-height: 1.5; color: ${colors.text.secondary};
  b { color: ${colors.text.primary}; }
`;
const SpamNote = styled.div`
  width: 100%; margin-bottom: 14px; padding: 10px 12px;
  background: ${colors.warning.main}12; border: 1px solid ${colors.warning.main}40;
  border-radius: 10px; font-size: 12.5px; line-height: 1.5; color: ${colors.text.secondary};
  b { color: ${colors.text.primary}; }
`;
const ResendOk = styled.div`
  display: flex; align-items: center; gap: 6px; margin-bottom: 8px;
  font-size: 12.5px; font-weight: ${typography.fontWeight.medium}; color: ${colors.success.main};
`;
const ExitRow = styled.div`
  display: flex; align-items: center; justify-content: center; flex-wrap: wrap;
  gap: 8px; margin-top: 16px;
`;
const ExitLink = styled.button`
  background: none; border: none; padding: 0; cursor: pointer;
  font-size: 12.5px; color: ${colors.text.secondary};
  &:hover:not(:disabled) { color: ${colors.primary.main}; text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const ExitDot = styled.span`
  color: ${colors.border.light}; font-size: 12px;
`;

// ── WhatsApp OTP (Track 2) ──────────────────────────────────────
const WaIconWrap = styled.div`
  width: 72px; height: 72px; border-radius: 50%;
  background: rgba(37, 211, 102, 0.12); color: #25D366;
  display: flex; align-items: center; justify-content: center; margin-bottom: 20px;
`;
const OtpRow = styled.div`
  display: flex; gap: 8px; justify-content: center; margin: 10px 0 14px; width: 100%;
`;
const OtpBox = styled.input`
  width: 46px; height: 54px; text-align: center;
  font-size: 22px; font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  border: 1.5px solid ${p => (p.$error ? colors.status.error : colors.border.light)};
  border-radius: 10px; background: #FAFAFA; outline: none; transition: all 0.2s;
  &:focus { border-color: #25D366; box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.15); background: #fff; }
  @media (max-width: 480px) { width: 40px; height: 48px; font-size: 20px; }
`;
const OtpErr = styled.div`
  font-size: 12.5px; color: ${colors.status.error}; margin-bottom: 10px; text-align: center;
`;
const SwitchRow = styled.div`
  display: flex; justify-content: center; margin-top: 14px;
`;
const SwitchLink = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; cursor: pointer;
  font-size: 13px; font-weight: ${typography.fontWeight.medium}; color: ${colors.primary.main};
  &:hover:not(:disabled) { text-decoration: underline; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const EditBanner = styled.div`
  margin-bottom: 16px; padding: 10px 12px;
  background: ${colors.primary.main}0D; border: 1px solid ${colors.primary.main}30;
  border-radius: 10px; font-size: 12.5px; line-height: 1.5; color: ${colors.text.secondary};
`;
const EditHint = styled.div`
  font-size: 11.5px; color: ${colors.text.tertiary}; margin-top: 2px;
`;

const Footer = styled.div`
  margin-top: 20px; color: rgba(255, 255, 255, 0.35);
  font-size: 12px; letter-spacing: 0.5px; z-index: 1;
`;

export default Signup;
